import { CommonModule } from '@angular/common';
import { Component, DoCheck, Input, OnChanges, OnDestroy, Optional, Self, SimpleChanges, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldAppearance, MatFormFieldModule, SubscriptSizing } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { TodayDatepickerHeaderComponent } from './today-datepicker-header.component';

type DatePickerErrorMessages = Partial<Record<string, string>>;

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './date-picker.component.html',
  styleUrl: './date-picker.component.scss',
})
export class DatePickerComponent implements ControlValueAccessor, DoCheck, OnChanges, OnDestroy {
  @Input({ required: true }) label!: string;
  @Input() placeholder = 'dd/mm/aaaa';
  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() subscriptSizing: SubscriptSizing = 'dynamic';
  @Input() fieldClass = '';
  @Input() compact = false;
  @Input() min: any | null = null;
  @Input() max: any | null = null;
  @Input() touchUi = false;
  @Input() errorMessages: DatePickerErrorMessages = {};

  readonly control = new FormControl<any | null>(null);
  readonly calendarHeaderComponent = TodayDatepickerHeaderComponent;
  readonly isMobile$ = inject(BreakpointObserver)
    .observe('(max-width: 720px)')
    .pipe(
      map(result => result.matches),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  private readonly destroy$ = new Subject<void>();
  private readonly datepickerErrorKeys = ['matDatepickerParse', 'matDatepickerMin', 'matDatepickerMax'];
  private onChange: (value: any | null) => void = () => {};
  private onTouched: () => void = () => {};

  get isRequired(): boolean {
    return !!this.ngControl?.control?.hasValidator(Validators.required);
  }

  constructor(@Optional() @Self() public readonly ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.control.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncDatepickerErrors());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['min'] || changes['max']) {
      this.control.updateValueAndValidity({ emitEvent: false });
      this.syncDatepickerErrors();
    }
  }

  ngDoCheck(): void {
    this.syncParentState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: any | null): void {
    this.control.setValue(value ?? null, { emitEvent: false });
  }

  registerOnChange(fn: (value: any | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  commitValue(value: any | null): void {
    this.onChange(value);
  }

  commitBeforeNavigation(event: KeyboardEvent): void {
    if (!event.altKey || !event.ctrlKey || event.metaKey || event.shiftKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
    this.commitValue(this.control.value);
    this.markAsTouched();
  }

  markAsTouched(): void {
    this.onTouched();
    this.ngControl?.control?.markAsTouched();
  }

  hasError(errorName: string): boolean {
    const parentControl = this.ngControl?.control;
    return !!(
      (parentControl?.touched || parentControl?.dirty || this.control.touched || this.control.dirty) &&
      (parentControl?.hasError(errorName) || this.control.hasError(errorName))
    );
  }

  getErrorMessage(errorName: string): string {
    return this.errorMessages[errorName] ?? this.defaultErrorMessages[errorName] ?? 'Fecha invalida';
  }

  private syncDatepickerErrors(): void {
    const parentControl = this.ngControl?.control;
    if (!parentControl) {
      return;
    }

    const currentErrors: ValidationErrors = { ...(parentControl.errors ?? {}) };
    let changed = false;

    for (const key of this.datepickerErrorKeys) {
      if (this.control.hasError(key)) {
        const error = this.control.getError(key) ?? true;
        if (currentErrors[key] !== error) {
          currentErrors[key] = error;
          changed = true;
        }
        continue;
      }

      if (key in currentErrors) {
        delete currentErrors[key];
        changed = true;
      }
    }

    if (changed) {
      parentControl.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
    }
  }

  private syncParentState(): void {
    const parentControl = this.ngControl?.control;
    if (!parentControl) {
      return;
    }

    if (parentControl.touched && !this.control.touched) {
      this.control.markAsTouched({ onlySelf: true });
    }

    if (parentControl.dirty && !this.control.dirty) {
      this.control.markAsDirty({ onlySelf: true });
    }

    this.syncParentError('required', parentControl.hasError('required'));
  }

  private syncParentError(errorName: string, hasError: boolean): void {
    const currentErrors: ValidationErrors = { ...(this.control.errors ?? {}) };

    if (hasError) {
      if (currentErrors[errorName]) {
        return;
      }

      currentErrors[errorName] = true;
      this.control.setErrors(currentErrors);
      return;
    }

    if (!(errorName in currentErrors)) {
      return;
    }

    delete currentErrors[errorName];
    this.control.setErrors(Object.keys(currentErrors).length ? currentErrors : null);
  }

  private readonly defaultErrorMessages: Record<string, string> = {
    required: 'Requerido',
    matDatepickerParse: 'Ingresa una fecha valida con formato dd/mm/aaaa.',
    matDatepickerMin: 'La fecha es anterior al minimo permitido.',
    matDatepickerMax: 'La fecha es posterior al maximo permitido.',
    beforeBirthDate: 'La fecha no puede ser anterior a la fecha de nacimiento.',
    futureBirthDate: 'La fecha no puede ser posterior a hoy.',
  };
}
